import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GlobalPlanInterface, GlobalPropertyInterface, GlobalUserInterface } from "Interface/interface";
import { apiRequest } from "services/ServiceUtil";
import { LIBRARY_SERVICE_METHOD, USER_PLAN_SERVICE_METHOD, USER_SERVICES_METHOD } from "services/commonService";

const SLICE = "GLOBAL_USER";

export const PLAN_TYPES = {
  PLAN: "PLAN",
  TEMPLATE_PLAN: "TEMPLATE_PLAN"
} as const;

export type PlanType = (typeof PLAN_TYPES)[keyof typeof PLAN_TYPES];

/** Library plan row; the multi-target search also returns the properties it is targeted at. */
export interface GlobalPlanOption extends GlobalPlanInterface {
  properties: string[];
}

/** Template Plan / Property Plan row returned by above-property/plans. */
export interface UserPlanOption {
  id: string;
  name: string;
  innCode: string;
  title?: string;
  /** hltIds as returned by the API, or resolved owner records once stored as a recent search. */
  owners: string[] | GlobalUserInterface[];
}

interface AssociatesResponse {
  users: GlobalUserInterface[];
  meta: { page: { total: number } };
}

interface GlobalPlansResponse {
  plans: GlobalPlanOption[];
}

interface InncodeSearchResponse {
  response: { properties: GlobalPropertyInterface[] };
  data: FetchGlobalInncodesArg;
}

interface InncodeInfoResponse {
  innCodeDetails: { innCode: string; propName: string; mgmtCompany: string }[];
}

interface AbovePropertyPlansResponse {
  userplans: UserPlanOption[];
  meta: { owners: string[] };
}

interface TemplatePropertyPlansResponse {
  userPlans: UserPlanOption[];
  ownerResponse: AssociatesResponse | null;
  planType: PlanType;
}

interface FetchGlobalUserArg {
  page: number;
  size: number;
  value: string;
  includecorpusers?: boolean;
  innCode?: string | string[];
}

interface FetchGlobalPlansArg {
  filter: { innCode: string | null; functionalAreaIds: string[]; filterIds: string[] };
  pagination: { page: number; size: number };
  searchText: string;
  planType: number[];
  sort: { property: string; direction: string } | null;
  externalHltId?: string;
}

interface FetchGlobalInncodesArg {
  searchText: string;
  isAbovePropertyUser: boolean;
  inncode: string;
}

interface FetchOwnerDetailsArg {
  size: number;
  value: string;
}

interface FetchMultiTargetedArg {
  innCodes: string[];
  filter: { functionalAreaIds: string[]; filterIds: string[]; ids: string[] };
  sort: null;
  pagination: { page: number; size: number };
  searchText: string;
  planType: number[];
  isSortingDisabled: boolean;
}

interface FetchInncodeInfoArg {
  inncodes: string[];
  propNameRequired: boolean;
  searchText: string;
}

export interface TemplatePropertyPlansArg {
  page: number;
  payload: {
    includeFilters: boolean;
    includeOwners: boolean;
    planType: PlanType;
    searchText: string;
    functionalArea: string;
    innCode: string;
    searchedHltId?: string;
  };
}

export let userList: GlobalUserInterface[] = [];

export function setUserList(newList: GlobalUserInterface[]) {
  userList = newList;
}

interface GlobalSearchState {
  globalUserDetail: GlobalUserInterface[];
  globalUserListLoading: boolean;
  totalGlobalUser: number;
  category: string;
  globalPlanList: GlobalPlanOption[];
  globalInncodeList: GlobalPropertyInterface[];
  templatePlanList: UserPlanOption[];
  propertyPlanList: UserPlanOption[];
  planOwnerList: GlobalUserInterface[];
  searchLoading: boolean;
  latestSearchRequestId: string | null;
}

const initialState: GlobalSearchState = {
  globalUserDetail: [],
  globalUserListLoading: true,
  totalGlobalUser: 0,
  category: "Property",
  globalPlanList: [],
  globalInncodeList: [],
  templatePlanList: [],
  propertyPlanList: [],
  planOwnerList: [],
  searchLoading: false,
  latestSearchRequestId: null
};

export const fetchGlobalUser = createAsyncThunk<AssociatesResponse, FetchGlobalUserArg>(
  `${SLICE}/fetchGlobalUser`,
  (data, thunkApi) => {
    const props = {
      innCodes: data?.innCode || "",
      page: data.page,
      size: data.size,
      searchText: data.value,
      includecorpusers: data?.includecorpusers ?? false
    };
    return apiRequest(USER_SERVICES_METHOD.getAssociates, {}, thunkApi, [], props);
  }
);

export const fetchGlobalPlansAsync = createAsyncThunk<GlobalPlansResponse, FetchGlobalPlansArg>(
  `${SLICE}/fetchGlobalPlansAsync`,
  (data, thunkApi) => {
    return apiRequest(LIBRARY_SERVICE_METHOD.postPlans, data, thunkApi, [], {
      externalHltId: data?.externalHltId || ""
    });
  }
);

export const fetchGlobalInncodes = createAsyncThunk<InncodeSearchResponse, FetchGlobalInncodesArg>(
  `${SLICE}/fetchGlobalInncodes`,
  async (data, thunkApi) => {
    const response = await apiRequest(LIBRARY_SERVICE_METHOD.getInncode, {}, thunkApi, [], {
      searchText: data.searchText
    });
    return {
      response,
      data
    };
  }
);

export const fetchOwnerDetailsAsync = createAsyncThunk<AssociatesResponse, FetchOwnerDetailsArg>(
  `${SLICE}/fetchOwnerDetailsAsync`,
  (data, thunkApi) =>
    apiRequest(USER_SERVICES_METHOD.getAssociates, {}, thunkApi, [], { size: data.size, hltIds: data.value })
);

export const fetchMultiTargeted = createAsyncThunk<GlobalPlansResponse, FetchMultiTargetedArg>(
  `${SLICE}/fetchOwnerDetailsAsync`,
  (data, thunkApi) => {
    return apiRequest(LIBRARY_SERVICE_METHOD.getMultiTargated, data, thunkApi);
  }
);

export const fetchInncodeInfo = createAsyncThunk<InncodeInfoResponse, FetchInncodeInfoArg>(
  `${SLICE}/inncodeInfo`,
  (data, thunkApi) => apiRequest(LIBRARY_SERVICE_METHOD.postInncodeInfo, data, thunkApi)
);

export const fetchTemplatePropertyPlansAsync = createAsyncThunk<
  TemplatePropertyPlansResponse,
  TemplatePropertyPlansArg
>(`${SLICE}/fetchTemplatePropertyPlansAsync`, async (data, thunkApi) => {
  const params = {
    page: data.page,
    size: 100
  };

  const payload = {
    ...data.payload
  };

  const plansResponse: AbovePropertyPlansResponse = await apiRequest(
    USER_PLAN_SERVICE_METHOD.postAboveProperty,
    payload,
    thunkApi,
    ["plans"],
    params
  );

  const userPlans = plansResponse?.userplans ?? [];
  const planOwners = plansResponse?.meta?.owners ?? [];

  let ownerResponse: AssociatesResponse | null = null;

  if (planOwners.length > 0 && data?.payload?.planType === PLAN_TYPES.PLAN) {
    ownerResponse = await apiRequest(USER_SERVICES_METHOD.getAssociates, {}, thunkApi, [], {
      size: planOwners.length,
      hltIds: planOwners
    });
  }

  return {
    userPlans,
    ownerResponse,
    planType: data?.payload?.planType
  };
});

export const GlobalSearchSlice = createSlice({
  name: SLICE,
  initialState,
  reducers: {
    clearSearch: (state) => {
      state.globalUserDetail = [];
    },
    clearPlanSearch: (state) => {
      state.globalPlanList = [];
    },
    clearInnCodeSearch: (state) => {
      state.globalInncodeList = [];
    },
    setCategory: (state, action) => {
      state.category = action.payload;
    },
    setTemplatePlanList: (state, action) => {
      state.templatePlanList = action.payload;
    },
    setPropertyPlanList: (state, action) => {
      state.propertyPlanList = action.payload;
    },
    setPlanOwner: (state, action) => {
      state.planOwnerList = action.payload;
    },
    setSearchLoadingCount: (state, action) => {
      state.searchLoading = action.payload;
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchGlobalUser.pending, (state) => {
        state.globalUserListLoading = true;
        state.searchLoading = true;
      })
      .addCase(fetchGlobalUser.fulfilled, (state, action) => {
        state.globalUserListLoading = false;
        state.totalGlobalUser = action.payload?.meta?.page?.total;
        const newUsers = action.payload?.users;
        const page = action?.meta?.arg?.page;
        userList = page === 0 ? newUsers : [...userList, ...newUsers];
        state.globalUserDetail = userList;
        state.searchLoading = false;
      })
      .addCase(fetchGlobalUser.rejected, (state) => {
        state.globalUserListLoading = false;
        state.globalUserDetail = [];
        state.searchLoading = false;
      })
      .addCase(fetchGlobalPlansAsync.pending, (state) => {
        state.searchLoading = true;
      })
      .addCase(fetchGlobalPlansAsync.fulfilled, (state, action) => {
        state.globalPlanList = action?.payload?.plans;
        state.searchLoading = false;
      })
      .addCase(fetchGlobalPlansAsync.rejected, (state) => {
        state.searchLoading = false;
      })
      .addCase(fetchGlobalInncodes.pending, (state) => {
        state.searchLoading = true;
      })
      .addCase(fetchGlobalInncodes.fulfilled, (state, action) => {
        const isAboverPropertyAndHotel = action?.payload?.data?.isAbovePropertyUser;
        if (isAboverPropertyAndHotel) {
          const innode = action?.payload?.data?.inncode.split(",");
          const filteredHotels = action?.payload?.response?.properties.filter((hotel) =>
            innode.includes(hotel.innCode)
          );
          state.globalInncodeList = filteredHotels ?? [];
        } else {
          state.globalInncodeList = action?.payload?.response?.properties;
        }
        state.searchLoading = false;
      })
      .addCase(fetchGlobalInncodes.rejected, (state) => {
        state.searchLoading = false;
      })
      .addCase(fetchMultiTargeted.pending, (state) => {
        state.searchLoading = true;
      })
      .addCase(fetchMultiTargeted.fulfilled, (state, action) => {
        state.globalPlanList = action.payload.plans;
        state.searchLoading = false;
      })
      .addCase(fetchMultiTargeted.rejected, (state) => {
        state.searchLoading = true;
      })
      .addCase(fetchTemplatePropertyPlansAsync.pending, (state, action) => {
        state.searchLoading = true;
        state.latestSearchRequestId = action.meta.requestId;
      })
      .addCase(fetchTemplatePropertyPlansAsync.fulfilled, (state, action) => {
        const { userPlans, ownerResponse, planType } = action.payload;
        // Ignore old request's response
        if (state.latestSearchRequestId !== action.meta.requestId) {
          return;
        }
        if (planType === PLAN_TYPES.PLAN) {
          state.propertyPlanList = userPlans;

          state.planOwnerList = ownerResponse?.users ?? [];
        } else if (planType === PLAN_TYPES.TEMPLATE_PLAN) {
          state.templatePlanList = userPlans;
        }
        state.searchLoading = false;
        state.latestSearchRequestId = null;
      })
      .addCase(fetchTemplatePropertyPlansAsync.rejected, (state, action) => {
        if (state.latestSearchRequestId !== action.meta.requestId) {
          return;
        }
        state.searchLoading = false;
        state.latestSearchRequestId = null;
      })
      .addCase(fetchInncodeInfo.pending, (state) => {
        state.searchLoading = true;
      })
      .addCase(fetchInncodeInfo.fulfilled, (state, action) => {
        const innCodeDetails = action.payload.innCodeDetails;
        const modifiedData = innCodeDetails.map((data) => ({
          innCode: data.innCode,
          name: data.propName,
          shortName: data.mgmtCompany
        }));
        state.globalInncodeList = modifiedData ?? [];
        state.searchLoading = false;
      })
      .addCase(fetchInncodeInfo.rejected, (state) => {
        state.searchLoading = false;
      });
  }
});

export const {
  clearSearch,
  setCategory,
  clearPlanSearch,
  clearInnCodeSearch,
  setTemplatePlanList,
  setPropertyPlanList,
  setPlanOwner,
  setSearchLoadingCount
} = GlobalSearchSlice.actions;

export default GlobalSearchSlice.reducer;
